/**
 * Audit Export Button Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Button with dropdown for exporting audit logs.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Download, FileText, FileJson, ChevronDown, Loader2 } from 'lucide-react';
import { auditService } from '../../../services/admin';
import type { AuditFilter, AuditExportFormat } from '../../../types/admin';
import { Button } from '../../ui/button';
import { toast } from 'sonner';

interface AuditExportButtonProps {
  filter: AuditFilter;
  disabled?: boolean;
}

const AuditExportButton: React.FC<AuditExportButtonProps> = ({
  filter,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState<AuditExportFormat | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleExport = async (format: AuditExportFormat) => {
    setIsExporting(true);
    setExportFormat(format);
    setIsOpen(false);

    try {
      await auditService.exportAndDownload({
        format,
        user_id: filter.user_id,
        event_types: filter.event_type ? [filter.event_type] : filter.event_types,
        date_from: filter.date_from,
        date_to: filter.date_to,
        severity: filter.severity,
        resource_type: filter.resource_type,
      });
      toast.success(`Audit logs exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export audit logs');
    } finally {
      setIsExporting(false);
      setExportFormat(null);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || isExporting}
      >
        {isExporting ? (
          <>
            <Loader2 size={16} className="mr-2 animate-spin" />
            Exporting {exportFormat?.toUpperCase()}...
          </>
        ) : (
          <>
            <Download size={16} className="mr-2" />
            Export
            <ChevronDown size={14} className="ml-1" />
          </>
        )}
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
          <button
            onClick={() => handleExport('csv')}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <FileText size={16} className="text-green-600" />
            <div className="text-left">
              <span className="font-medium">CSV Format</span>
              <span className="block text-xs text-gray-500">
                Spreadsheet-compatible
              </span>
            </div>
          </button>
          <button
            onClick={() => handleExport('json')}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <FileJson size={16} className="text-blue-600" />
            <div className="text-left">
              <span className="font-medium">JSON Format</span>
              <span className="block text-xs text-gray-500">
                NDJSON (newline-delimited)
              </span>
            </div>
          </button>

          <div className="border-t border-gray-200 mt-1 pt-1 px-4 py-2">
            <p className="text-xs text-gray-500">
              Export applies current filters
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditExportButton;
